import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';

export interface BarListItem {
  label: string;
  value: number;
  /** Optional muted hint shown after the label (e.g. a continent). */
  hint?: string;
  /** When set, the row label links here via react-router. */
  href?: string;
}

interface BarListProps {
  items: BarListItem[];
  /** Denominator for the bar widths. Defaults to the largest value. */
  max?: number;
  /** Formats the right-aligned value. Defaults to a plain number. */
  valueFormat?: (value: number) => string;
  /** Tailwind background class for the filled bar. */
  colorClass?: string;
  emptyMessage?: string;
}

/**
 * Horizontal bar rows: a label over a proportional background bar, with the
 * value right-aligned. Visual language matches the stacked bar in
 * summary-cards.tsx — flat, light, no chart dependency.
 */
export function BarList({
  items,
  max,
  valueFormat = (v) => String(v),
  colorClass = 'bg-indigo-500',
  emptyMessage = 'No data.',
}: BarListProps): JSX.Element {
  if (items.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">{emptyMessage}</p>;
  }

  const denom = Math.max(max ?? Math.max(...items.map((i) => i.value)), 1);

  return (
    <ul className="space-y-2">
      {items.map((item) => {
        const pct = Math.max(0, Math.min(100, (item.value / denom) * 100));
        const label = item.href ? (
          <Link to={item.href} className="hover:underline">
            {item.label}
          </Link>
        ) : (
          item.label
        );

        return (
          <li key={item.label} className="space-y-1">
            <div className="flex items-baseline justify-between gap-2 text-sm">
              <span className="min-w-0 truncate">
                {label}
                {item.hint && <span className="ml-1.5 text-xs text-muted-foreground">{item.hint}</span>}
              </span>
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {valueFormat(item.value)}
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className={cn('h-full rounded-full', colorClass)}
                style={{ width: `${pct}%` }}
                aria-hidden
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
